import type { HealthResponse } from '@crew/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useAdmin, useAuth } from '@/auth/AuthProvider';
import { LogoutIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { currentPushState, disablePush, enablePush, PUSH_STATE_TEXT, type PushState } from '@/services/push';
import { errorMessage } from '@/services/apiError';
import { api, ApiClientError } from '@/services/api';
import { requestPasswordReset } from '@/services/authService';

type Health = { state: 'checking' } | { state: 'ok'; data: HealthResponse } | { state: 'down'; message: string };

export function SettingsPage() {
  const { me } = useAdmin();
  const { signOut } = useAuth();
  const [health, setHealth] = useState<Health>({ state: 'checking' });
  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .health(ctrl.signal)
      .then((data) => setHealth({ state: 'ok', data }))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setHealth({ state: 'down', message: err instanceof ApiClientError ? err.message : 'השרת לא הגיב' });
      });
    return () => ctrl.abort();
  }, []);

  async function sendReset() {
    if (!me.email) return;
    setResetState('sending');
    try {
      await requestPasswordReset(me.email);
      setResetState('sent');
    } catch {
      setResetState('error');
    }
  }

  return (
    <>
      <PageHeader title="הגדרות" />

      <div className="space-y-5">
        <Section title="החשבון שלי">
          <dl className="divide-y divide-line">
            <Row label="שם">{me.displayName}</Row>
            <Row label="אימייל">
              <span className="ltr">{me.email ?? '—'}</span>
            </Row>
            <Row label="הרשאה">מנהל, הרשאות מלאות</Row>
          </dl>
          <div className="flex flex-wrap gap-3 pt-4">
            <Button variant="secondary" onClick={() => void sendReset()} loading={resetState === 'sending'} disabled={!me.email}>
              שינוי סיסמה
            </Button>
            <Button variant="secondary" icon={<LogoutIcon size={18} />} onClick={() => void signOut()}>
              יציאה
            </Button>
          </div>
          {resetState === 'sent' && (
            <div className="pt-3">
              <Notice tone="success">נשלח אליך אימייל עם קישור לבחירת סיסמה חדשה.</Notice>
            </div>
          )}
          {resetState === 'error' && (
            <div className="pt-3">
              <Notice tone="error">שליחת האימייל נכשלה. יש לנסות שוב.</Notice>
            </div>
          )}
        </Section>

        <Section title="מצב המערכת">
          <dl className="divide-y divide-line">
          <Row label="שרת">
            {health.state === 'checking' && <span className="text-ink-3">בודקים…</span>}
            {health.state === 'ok' && (
              <span className="inline-flex items-center gap-2 text-ok">
                <span className="size-2 rounded-full bg-ok" aria-hidden="true" />
                מחובר, גרסה <span className="ltr">{health.data.version}</span>
              </span>
            )}
            {health.state === 'down' && (
              <span className="inline-flex items-center gap-2 text-danger">
                <span className="size-2 rounded-full bg-danger" aria-hidden="true" />
                {health.message}
              </span>
            )}
          </Row>
          <Row label="גרסת האפליקציה">
            <span className="ltr">{__APP_VERSION__}</span>
          </Row>
          </dl>
        </Section>

        <PushSection />
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface p-5">
      <h2 className="mb-2 text-[16px] font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 text-[15px]">
      <dt className="text-ink-2">{label}</dt>
      <dd className="min-w-0 truncate text-end font-medium">{children}</dd>
    </div>
  );
}

function PushSection() {
  const { user } = useAdmin();
  const [state, setState] = useState<PushState>(() => currentPushState());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canToggle = state === 'on' || state === 'off';

  return (
    <Section title="התראות לטלפון">
      <p className="text-[14px] text-ink-2">{PUSH_STATE_TEXT[state]}</p>
      {error && (
        <div className="pt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      {canToggle && (
        <div className="pt-4">
          <Button
            variant={state === 'on' ? 'secondary' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setState(state === 'on' ? await disablePush(user) : await enablePush(user));
              } catch (err) {
                setError(errorMessage(err, 'הפעלת ההתראות נכשלה'));
              } finally {
                setBusy(false);
              }
            }}
          >
            {state === 'on' ? 'כיבוי התראות במכשיר הזה' : 'הפעלת התראות במכשיר הזה'}
          </Button>
        </div>
      )}
    </Section>
  );
}
